import { ArrowSquareOutIcon, CalendarBlankIcon, MapPinIcon, SolarRoofIcon } from "@phosphor-icons/react";
import { ScrollableCards } from "../ScrollableCards";
import { handleCardCtaClick } from "../../MobileApp/webviewBridge";
import type { Tournament } from "./validate";
export type { Tournament, TournamentResult } from "./validate";

// Ported from the tournament card on courtsapp.com — same icons (Phosphor
// SolarRoof / MapPin / CalendarBlank / ArrowSquareOut), same badge, same
// "Registration cost" chip, same full-width dark pill. Values were read out of
// the site's own bundle rather than eyeballed off a screenshot, so the mint
// chip is the real rgba(38,209,151,0.5) and not a lookalike.
//
// This is the one card in the widget that does NOT use the lime CTA — the site
// uses dark green here and matching it was the point.

const ICON_SIZE = 16;
const ICON_COLOR = "#586577";

function InfoRow({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <div className="lk-tournament-row">
      {icon}
      <span className="lk-tournament-row-text">{text}</span>
    </div>
  );
}

function TournamentCard({ event }: { event: Tournament }) {
  // The site always shows a status badge; only the closing one is urgent.
  const badgeClass = event.closingSoon
    ? "lk-tournament-badge lk-tournament-badge--closing"
    : "lk-tournament-badge lk-tournament-badge--open";

  return (
    <a
      className="lk-product-card lk-tournament-card"
      href={event.url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`${event.name} at ${event.venueName || event.location}. Opens the organiser's site outside CourtsApp.`}
      onClick={(e) => handleCardCtaClick(e, event.url)}
    >
      <div className="lk-tournament-banner">
        {event.bannerUrl && (
          <div
            className="lk-tournament-banner-image"
            style={{ backgroundImage: `url(${event.bannerUrl})` }}
            role="img"
            aria-label={event.name}
          />
        )}
        <span className={badgeClass}>{event.closingSoon ? "Closing soon" : "Open"}</span>
      </div>

      <div className="lk-tournament-body">
        <div className="lk-tournament-info">
          <div className="lk-tournament-title-block">
            {event.categoryLabel && (
              <div className="lk-tournament-eyebrow">{event.categoryLabel}</div>
            )}
            <div className="lk-tournament-title">{event.name}</div>
          </div>
          <div className="lk-tournament-rows">
            {event.venueName && (
              <InfoRow
                icon={<SolarRoofIcon size={ICON_SIZE} color={ICON_COLOR} weight="regular" />}
                text={event.venueName}
              />
            )}
            {event.location && (
              <InfoRow
                icon={<MapPinIcon size={ICON_SIZE} color={ICON_COLOR} weight="regular" />}
                text={event.location}
              />
            )}
            {event.dates && (
              <InfoRow
                icon={<CalendarBlankIcon size={ICON_SIZE} color={ICON_COLOR} weight="regular" />}
                text={event.dates}
              />
            )}
          </div>
        </div>

        <div className="lk-tournament-footer">
          <div className="lk-tournament-price-block">
            <span className="lk-tournament-price-tag">Registration cost</span>
            {/* Their own fallback string — an empty price means the feed had none. */}
            <span className="lk-tournament-price">{event.price || "Not listed"}</span>
          </div>
          <span className="lk-tournament-cta">
            View Tournament
            <ArrowSquareOutIcon size={18} color="#fff" weight="regular" />
          </span>
        </div>
      </div>
    </a>
  );
}

export function TournamentCards({ tournaments }: { tournaments: Tournament[] }) {
  if (tournaments.length === 0) return null;
  if (tournaments.length === 1) return <TournamentCard event={tournaments[0]} />;
  return (
    <ScrollableCards className="lk-product-cards">
      {tournaments.map((event, i) => (
        <TournamentCard key={`${event.name}-${i}`} event={event} />
      ))}
    </ScrollableCards>
  );
}
